/* The Issues section (designs/issue-tracker.md IT9). One list from
   GET /api/issues: a requester's own reports ("mine"), or every report for an
   Admin ("all") — the server decides, and the scope travels with the list so
   an empty one is never read as "nobody has reported anything" (IT6).

   An Admin narrows the list (client-side, filterIssues), exports what is on
   screen, and triages one report in a side drawer: state, priority, the one
   Jira or GitHub link, a note to the requester, and the history of every
   change. A requester's drawer is read-only. */
import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import { createPortal } from "react-dom";
import { useModal } from "../shell/useModal";
import type { Stamp } from "../../../shared/organizer-roles";
import { ENTRY_PATH_PHRASE } from "../../../shared/organizer-roles";
import {
  EMPTY_ISSUE_FILTERS,
  ISSUE_KIND_LABEL,
  ISSUE_LIMITS,
  ISSUE_PRIORITIES,
  ISSUE_PRIORITY_LABEL,
  ISSUE_STATES,
  ISSUE_STATE_LABEL,
  NOT_LINKED,
  NOT_SET,
  filterIssues,
  hasIssueFilters,
  isOpenState,
  issueCsv,
  type IssueDetail,
  type IssueFilters,
  type IssueList,
  type IssuePriority,
  type IssueState,
  type IssueView,
  type TrackerLink,
  type Triage,
} from "../../../shared/issues";
import { asJson } from "../intake/api";
import { ViewFrame } from "../shell/ViewFrame";

const isList = (v: unknown): v is IssueList =>
  typeof v === "object" && v !== null && Array.isArray((v as IssueList).issues);

const getIssues = async (): Promise<IssueList> => {
  const v = await asJson(await fetch("/api/issues"));
  if (!isList(v)) throw new Error("the issue list came back in an unexpected shape");
  return v;
};

const getHistory = async (id: string): Promise<Triage[]> => {
  const v = await asJson<IssueDetail>(await fetch(`/api/issues/${encodeURIComponent(id)}`));
  return Array.isArray(v?.history) ? v.history : [];
};

const putTriage = async (id: string, body: object): Promise<IssueView> =>
  asJson(
    await fetch(`/api/issues/${encodeURIComponent(id)}/triage`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  );

const day = (s: Stamp): string => s.date.slice(0, 10);

function StateTag({ state }: { state: IssueState }): JSX.Element {
  return <span className={`state issue-state ${isOpenState(state) ? "open" : "closed"} ${state}`}>{ISSUE_STATE_LABEL[state]}</span>;
}

/* the link is an allowlisted Jira or GitHub URL (IT5) — still opened with
   noopener so the target cannot reach back into Hermes */
function TrackerAnchor({ tracker }: { tracker: TrackerLink }): JSX.Element {
  return (
    <a href={tracker.url} target="_blank" rel="noopener noreferrer">
      {tracker.label} ({tracker.type === "jira" ? "Jira" : "GitHub"})
    </a>
  );
}

function Filters({
  f,
  set,
  filers,
  showFiler,
}: {
  f: IssueFilters;
  set: (f: IssueFilters) => void;
  filers: { userId: string; name: string }[];
  showFiler: boolean;
}): JSX.Element {
  return (
    <div className="issue-filters">
      <label className="field">
        <span className="field-label">Kind</span>
        <select id="issue-f-kind" value={f.kind} onChange={(e) => set({ ...f, kind: e.target.value as IssueFilters["kind"] })}>
          <option value="all">All</option>
          <option value="bug">Bugs</option>
          <option value="feature">Feature requests</option>
        </select>
      </label>
      <label className="field">
        <span className="field-label">State</span>
        <select id="issue-f-state" value={f.state} onChange={(e) => set({ ...f, state: e.target.value as IssueFilters["state"] })}>
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          {ISSUE_STATES.map((s) => (
            <option key={s} value={s}>
              {ISSUE_STATE_LABEL[s]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Priority</span>
        <select
          id="issue-f-priority"
          value={f.priority}
          onChange={(e) => set({ ...f, priority: e.target.value as IssueFilters["priority"] })}
        >
          <option value="all">All</option>
          {ISSUE_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {ISSUE_PRIORITY_LABEL[p]}
            </option>
          ))}
          <option value="none">{NOT_SET}</option>
        </select>
      </label>
      <label className="field">
        <span className="field-label">Tracker</span>
        <select
          id="issue-f-tracker"
          value={f.tracker}
          onChange={(e) => set({ ...f, tracker: e.target.value as IssueFilters["tracker"] })}
        >
          <option value="all">All</option>
          <option value="jira">Jira</option>
          <option value="github">GitHub</option>
          <option value="none">{NOT_LINKED}</option>
        </select>
      </label>
      {showFiler && (
        <label className="field">
          <span className="field-label">Filed by</span>
          <select id="issue-f-filer" value={f.filer} onChange={(e) => set({ ...f, filer: e.target.value })}>
            <option value="all">Anyone</option>
            {filers.map((p) => (
              <option key={p.userId} value={p.userId}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field issue-f-search">
        <span className="field-label">Search</span>
        <input
          id="issue-f-text"
          type="search"
          placeholder="Words in the title or description"
          value={f.text}
          onChange={(e) => set({ ...f, text: e.target.value })}
        />
      </label>
    </div>
  );
}

function TriageForm({
  issue,
  others,
  onSaved,
}: {
  issue: IssueView;
  others: IssueView[];
  onSaved: (v: IssueView) => void;
}): JSX.Element {
  const [state, setState] = useState<IssueState>(issue.state);
  const [priority, setPriority] = useState<IssuePriority | "">(issue.priority ?? "");
  const [url, setUrl] = useState(issue.tracker?.url ?? "");
  const [note, setNote] = useState(issue.resolution ?? "");
  const [dup, setDup] = useState(issue.duplicateOfId ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await putTriage(issue.id, {
          state,
          priority: priority === "" ? null : priority,
          trackerUrl: url.trim() === "" ? null : url.trim(),
          resolution: note.trim() === "" ? null : note,
          ...(state === "duplicate" ? { duplicateOfId: dup === "" ? null : dup } : {}),
          /* the revision this was made from — a stale one is refused (IT4a) */
          basedOn: issue.revision,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="issue-triage"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <h3>Triage</h3>
      <label className="field">
        <span className="field-label">State</span>
        <select id="issue-t-state" value={state} onChange={(e) => setState(e.target.value as IssueState)}>
          {ISSUE_STATES.map((s) => (
            <option key={s} value={s}>
              {ISSUE_STATE_LABEL[s]}
            </option>
          ))}
        </select>
      </label>
      {state === "duplicate" && (
        <label className="field">
          <span className="field-label">Duplicate of</span>
          <select id="issue-t-dup" value={dup} onChange={(e) => setDup(e.target.value)}>
            <option value="">Choose the earlier report…</option>
            {others.map((o) => (
              <option key={o.id} value={o.id}>
                {o.title} ({day(o.filed)})
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="field">
        <span className="field-label">Priority</span>
        <select id="issue-t-priority" value={priority} onChange={(e) => setPriority(e.target.value as IssuePriority | "")}>
          <option value="">{NOT_SET}</option>
          {ISSUE_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {ISSUE_PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span className="field-label">Tracker link</span>
        <input
          id="issue-t-url"
          type="url"
          placeholder="https://<site>.atlassian.net/browse/KEY-123 or https://github.com/<owner>/<repo>/issues/45"
          maxLength={ISSUE_LIMITS.url}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Note to the requester (needed for Declined or Duplicate)</span>
        <textarea
          id="issue-t-note"
          rows={3}
          maxLength={ISSUE_LIMITS.resolution}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <p className="form-error" role="alert">
        {error ?? ""}
      </p>
      <div className="form-actions">
        <button className="btn primary" type="submit" disabled={busy}>
          Save
        </button>
      </div>
    </form>
  );
}

function historyLine(t: Triage): string {
  const parts = [ISSUE_STATE_LABEL[t.state], t.priority ? ISSUE_PRIORITY_LABEL[t.priority] : NOT_SET];
  if (t.trackerUrl) parts.push(t.trackerUrl);
  return parts.join(" · ");
}

function IssueDrawer({
  issue,
  admin,
  others,
  onSaved,
  onClose,
}: {
  issue: IssueView;
  admin: boolean;
  others: IssueView[];
  onSaved: (v: IssueView) => void;
  onClose: () => void;
}): JSX.Element {
  const [history, setHistory] = useState<Triage[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const aside = useRef<HTMLElement>(null);
  useModal(aside, onClose);
  useEffect(() => {
    if (!admin) return;
    getHistory(issue.id)
      .then(setHistory)
      .catch((e: unknown) => setHistoryError(e instanceof Error ? e.message : String(e)));
  }, [admin, issue.id]);

  const saved = (v: IssueView): void => {
    onSaved(v);
    if (v.triaged)
      setHistory((h) => [
        ...(h ?? []),
        {
          seq: v.revision,
          state: v.state,
          priority: v.priority,
          trackerUrl: v.tracker?.url ?? null,
          resolution: v.resolution,
          duplicateOfId: v.duplicateOfId,
          recorded: v.triaged!,
        },
      ]);
  };
  const by = issue.filed.by?.name ?? "someone not recorded";
  const via = issue.filed.via ? ` ${ENTRY_PATH_PHRASE[issue.filed.via]}` : "";
  const dupOf = issue.duplicateOfId ? others.find((o) => o.id === issue.duplicateOfId) : undefined;

  /* a portal of its own, so useModal can make the rest of the page inert */
  return createPortal(
    <div className="modal-root">
      <div className="side-drawer-backdrop" onClick={onClose} />
      <aside
        className="side-drawer open issue-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="issue-drawer-title"
        ref={aside}
        tabIndex={-1}
      >
        <h2 id="issue-drawer-title">{issue.title}</h2>
        <p className="stamp">
          {ISSUE_KIND_LABEL[issue.kind]} · filed by {by}
          {via}, {day(issue.filed)}
          {issue.context.view ? ` · on ${issue.context.view}` : ""}
        </p>
        <dl className="issue-facts">
          <dt>State</dt>
          <dd>
            <StateTag state={issue.state} />
            {dupOf && <> of “{dupOf.title}”</>}
          </dd>
          <dt>Priority</dt>
          <dd>{issue.priority ? ISSUE_PRIORITY_LABEL[issue.priority] : <span className="not-set">{NOT_SET}</span>}</dd>
          <dt>Tracked in</dt>
          <dd>{issue.tracker ? <TrackerAnchor tracker={issue.tracker} /> : <span className="not-set">{NOT_LINKED}</span>}</dd>
          {issue.resolution && (
            <>
              <dt>Note</dt>
              <dd>{issue.resolution}</dd>
            </>
          )}
        </dl>
        <h3>{issue.kind === "bug" ? "What happened" : "What they would like"}</h3>
        <p className="issue-text">{issue.description}</p>
        {issue.steps && (
          <>
            <h3>How to make it happen again</h3>
            <p className="issue-text">{issue.steps}</p>
          </>
        )}
        {issue.expected && (
          <>
            <h3>What they expected</h3>
            <p className="issue-text">{issue.expected}</p>
          </>
        )}
        {admin && (
          <>
            <TriageForm key={issue.triaged?.date ?? "new"} issue={issue} others={others} onSaved={saved} />
            <h3>History</h3>
            {historyError !== null && <p className="capture-error">{historyError}</p>}
            {history !== null && history.length === 0 && <p className="not-set">Not triaged yet.</p>}
            {history !== null && history.length > 0 && (
              <ol className="issue-history">
                {history.map((t, i) => (
                  <li key={i}>
                    {historyLine(t)} — {t.recorded.by?.name ?? "not recorded"}, {day(t.recorded)}
                    {t.resolution ? <div className="issue-history-note">{t.resolution}</div> : null}
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
        <div className="form-actions">
          <button className="btn ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </aside>
    </div>,
    document.body,
  );
}

export function IssuesView(): JSX.Element {
  const [list, setList] = useState<IssueList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [f, setF] = useState<IssueFilters>(EMPTY_ISSUE_FILTERS);
  const [openId, setOpenId] = useState<string | null>(null);

  useEffect(() => {
    getIssues()
      .then(setList)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  const rows = list?.issues ?? [];
  const admin = list?.scope === "all";
  const shown = useMemo(() => filterIssues(rows, f), [rows, f]);
  const filers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) if (r.filed.by) seen.set(r.filed.by.userId, r.filed.by.name);
    return [...seen].map(([userId, name]) => ({ userId, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  const open = rows.find((r) => r.id === openId) ?? null;

  const replace = (v: IssueView): void =>
    setList((l) => (l ? { ...l, issues: l.issues.map((r) => (r.id === v.id ? v : r)) } : l));

  /* the rows on screen, as CSV — the same anchor-click download the intake
     board uses (intake-ui-parity UP6) */
  const doExport = (): void => {
    const url = URL.createObjectURL(new Blob([issueCsv(shown)], { type: "text/csv;charset=utf-8" }));
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = "hermes-issues.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const openCount = rows.filter((r) => isOpenState(r.state)).length;
  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>{admin ? "All issues." : "My issues."}</b>
          <span>
            {admin
              ? "Every bug and feature request filed about Hermes, from the web app and through Claude."
              : "The bugs and feature requests you have filed about Hermes, and what happened to each."}
          </span>
        </div>
      }
    >
      {error !== null && (
        <p className="capture-error" role="alert">
          The issues could not be read: {error}
        </p>
      )}
      {list !== null && (
        <section className="issues">
          <div className="issues-bar">
            <span className="count">
              {rows.length} {rows.length === 1 ? "report" : "reports"} · {openCount} open
            </span>
            <span className="spacer" />
            {hasIssueFilters(f) && (
              <button className="btn ghost small" onClick={() => setF(EMPTY_ISSUE_FILTERS)}>
                Clear filters
              </button>
            )}
            {admin && (
              <button className="btn small" onClick={doExport} disabled={shown.length === 0}>
                Export CSV
              </button>
            )}
          </div>
          {rows.length > 0 && <Filters f={f} set={setF} filers={filers} showFiler={admin} />}
          {rows.length === 0 ? (
            <p className="empty">
              {admin
                ? "Nobody has reported anything yet."
                : "You have not reported anything yet. Use “Report a problem or idea” at the top of any page."}
            </p>
          ) : shown.length === 0 ? (
            <p className="empty">Nothing matches these filters.</p>
          ) : (
            <div className="dash-table">
              <table className="recs issues-table">
                <thead>
                  <tr>
                    <th>Filed</th>
                    <th>Kind</th>
                    <th>Title</th>
                    <th>State</th>
                    <th>Priority</th>
                    <th>Tracker</th>
                    {admin && <th>Filed by</th>}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.id} className="rec">
                      <td className="num">{day(r.filed)}</td>
                      <td>{ISSUE_KIND_LABEL[r.kind]}</td>
                      <td>
                        <button className="btn link issue-open" onClick={() => setOpenId(r.id)}>
                          {r.title}
                        </button>
                      </td>
                      <td>
                        <StateTag state={r.state} />
                      </td>
                      <td>{r.priority ? ISSUE_PRIORITY_LABEL[r.priority] : <span className="not-set">{NOT_SET}</span>}</td>
                      <td>{r.tracker ? r.tracker.label : <span className="not-set">{NOT_LINKED}</span>}</td>
                      {admin && <td>{r.filed.by?.name ?? ""}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {open && (
        <IssueDrawer
          key={open.id}
          issue={open}
          admin={admin}
          others={rows.filter((r) => r.id !== open.id)}
          onSaved={replace}
          onClose={() => setOpenId(null)}
        />
      )}
    </ViewFrame>
  );
}
